<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import type { ClosureReadiness, ClosureReport } from '@/types/domain';
import { errorMessage } from '@/utils/format';

/*
  移交量产（0828 Tony:「项目完成，也就是项目移交量产……能不能做一些有仪式感的动作?」）。

  在这之前，「结项」只是模块中心抽屉里一个和「暂停」并排的状态下拉，
  不检查任何东西，改完也什么都不发生——所以 15 个项目全挂在「进行中」，
  两个 GATE6 逾期一个月没人填。

  仪式感的分量不在动画，在于**系统能说出这个项目做了什么**：
  周期多长、走过几个 GATE、多少变更多少交付物、团队都有谁。
  所以这个面板的主角是那份报告，门禁只是它前面的一道台阶。
*/
const props = defineProps<{ projectId: number | string; canManage: boolean; isDirector: boolean }>();
const emit = defineEmits<{ (event: 'closed'): void }>();

const loading = ref(false);
const busy = ref(false);
const readiness = ref<ClosureReadiness | null>(null);
const report = ref<ClosureReport | null>(null);
const note = ref('');
const waiverNotes = ref<Record<string, string>>({});
const showReport = ref(false);

const closed = computed(() => report.value?.closed === true);
const pending = computed(() => readiness.value?.pending ?? null);
const blocked = computed(() => readiness.value?.blocked ?? []);
const waivable = computed(() => readiness.value?.waivable ?? []);
const stats = computed(() => report.value?.stats ?? {});

/** 每条软指标都写够 5 个字才让提交——「ok」放过去，半年后查等于没写 */
const waiversReady = computed(() =>
  waivable.value.every((item) => (waiverNotes.value[item.key] ?? '').trim().length >= 5));

async function load(): Promise<void> {
  loading.value = true;
  try {
    const [state, data] = await Promise.all([
      eqsApi.projectClosureReadiness(props.projectId),
      eqsApi.projectClosureReport(props.projectId),
    ]);
    readiness.value = state;
    report.value = data;
    if (data.closed) showReport.value = true;
  } catch (error) {
    notify('error', '读取结项状态失败', errorMessage(error));
  } finally {
    loading.value = false;
  }
}

watch(() => props.projectId, load, { immediate: true });

async function submit(): Promise<void> {
  const ok = await confirmAction({
    title: '发起移交量产',
    message: '提交后由项目总监拍板。签字生效前项目状态不变。',
  });
  if (!ok) return;
  busy.value = true;
  try {
    await eqsApi.submitProjectClosure(props.projectId, {
      note: note.value.trim() || undefined,
      waivers: waivable.value.map((item) => ({ key: item.key, note: waiverNotes.value[item.key] ?? '' })),
    });
    notify('success', '已提交', '项目总监会收到待拍板通知。');
    note.value = '';
    await load();
  } catch (error) {
    notify('error', '提交失败', errorMessage(error));
  } finally {
    busy.value = false;
  }
}

async function decide(approve: boolean): Promise<void> {
  if (!pending.value) return;
  const ok = await confirmAction({
    title: approve ? '确认移交量产' : '退回结项申请',
    message: approve
      ? '签字后项目转为「已关闭」，并向项目团队和管理层发出移交通知。这一步不可撤销。'
      : '退回后发起人可以处理完再提。',
  });
  if (!ok) return;
  busy.value = true;
  try {
    await eqsApi.decideProjectClosure(props.projectId, pending.value.id, {
      approve, note: note.value.trim() || undefined,
    });
    notify('success', approve ? '已移交量产' : '已退回', approve ? '通知已发给项目团队。' : '发起人会收到通知。');
    note.value = '';
    await load();
    if (approve) emit('closed');
  } catch (error) {
    notify('error', '操作失败', errorMessage(error));
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <section class="closure" :class="{ 'is-closed': closed }">
    <header class="closure-head">
      <div>
        <span class="closure-eyebrow">{{ closed ? '已移交量产' : '移交量产' }}</span>
        <h3>{{ closed ? '这个项目已经交出去了' : '项目结项' }}</h3>
        <p v-if="closed" class="closure-sign">
          {{ report?.decidedBy }} 于 {{ report?.decidedAt?.slice(0, 10) }} 拍板
          <template v-if="report?.submitter">· {{ report.submitter }} 发起</template>
        </p>
        <p v-else class="closure-hint">
          GATE6 通过、工程变更全部关闭之后才能发起；由项目总监签字生效。
        </p>
      </div>
      <button class="button" type="button" :disabled="loading" @click="load">
        {{ loading ? '读取中…' : '⟳ 重新体检' }}
      </button>
    </header>

    <!-- 已结项：报告就是主角，门禁那套不用再摆 -->
    <div v-if="closed" class="closure-report">
      <div class="closure-timeline">
        <div><b>{{ report?.started_on || '—' }}</b><span>启动</span></div>
        <div><b>{{ report?.handover_on || '—' }}</b><span>移交 GATE6</span></div>
        <div><b>{{ report?.sop_on || '—' }}</b><span>SOP</span></div>
      </div>
      <dl class="closure-stats">
        <div><dt>计划节点</dt><dd>{{ stats.nodes_done ?? 0 }} / {{ stats.nodes ?? 0 }}</dd></div>
        <div><dt>交付物</dt><dd>{{ stats.deliverables_done ?? 0 }} / {{ stats.deliverables ?? 0 }}</dd></div>
        <div><dt>工程变更</dt><dd>{{ stats.changes ?? 0 }} 次</dd></div>
        <div><dt>项目会议</dt><dd>{{ stats.meetings ?? 0 }} 场</dd></div>
      </dl>
      <p v-if="report?.decisionNote" class="closure-note">拍板留言：{{ report.decisionNote }}</p>
      <!-- 当初放行了什么要留在报告里，不能结完就看不见 -->
      <div v-if="report?.waivers?.length" class="closure-waived">
        <h4>结项时放行的事项</h4>
        <ul>
          <li v-for="item in report.waivers" :key="item.key">
            <b>{{ item.label }}</b>（{{ item.count }} 项）：{{ item.note }}
          </li>
        </ul>
      </div>
      <div v-if="report?.team?.length" class="closure-team">
        <h4>项目团队（{{ report.team.length }} 人）</h4>
        <span v-for="member in report.team" :key="member.display_name">
          {{ member.display_name }}<em>{{ member.role_title }}</em>
        </span>
      </div>
    </div>

    <template v-else>
      <!-- 体检：硬指标红了直接说不能结，软指标列出来让人写理由 -->
      <ul class="closure-checks">
        <li v-for="item in readiness?.checks ?? []" :key="item.key" :class="[item.level, { ok: item.passed }]">
          <span class="closure-mark">{{ item.passed ? '✓' : item.level === 'hard' ? '✕' : '!' }}</span>
          <div>
            <b>{{ item.label }}</b>
            <span v-if="!item.passed" class="closure-count">还有 {{ item.count }} 项</span>
            <p v-if="!item.passed">{{ item.hint }}</p>
          </div>
        </li>
      </ul>

      <p v-if="blocked.length" class="closure-blocked">
        还不能结项：{{ blocked.map((item) => item.label).join('、') }}。先把这些做完。
      </p>

      <!-- 已经提交在等签字 -->
      <div v-else-if="pending" class="closure-pending">
        <p><b>等待项目总监拍板</b> —— {{ pending.submitter }} 于 {{ pending.submittedAt?.slice(0, 10) }} 发起</p>
        <p v-if="pending.note" class="closure-note">{{ pending.note }}</p>
        <template v-if="isDirector">
          <textarea v-model="note" rows="2" placeholder="拍板留言（选填）"></textarea>
          <div class="closure-actions">
            <button class="button" type="button" :disabled="busy" @click="decide(false)">退回</button>
            <button class="button button-primary" type="button" :disabled="busy" @click="decide(true)">
              {{ busy ? '处理中…' : '签字，确认移交量产' }}
            </button>
          </div>
        </template>
        <p v-else class="closure-hint">签字要项目总监来做。</p>
      </div>

      <!-- 可以发起 -->
      <div v-else-if="canManage" class="closure-submit">
        <div v-if="waivable.length" class="closure-waivers">
          <h4>这几项没做完，各写一句为什么可以放</h4>
          <label v-for="item in waivable" :key="item.key">
            <span>{{ item.label }}（还有 {{ item.count }} 项）</span>
            <input v-model="waiverNotes[item.key]" type="text" placeholder="例：剩余节点转量产维护跟进">
          </label>
        </div>
        <textarea v-model="note" rows="2" placeholder="结项说明（选填）"></textarea>
        <div class="closure-actions">
          <button
            class="button button-primary" type="button"
            :disabled="busy || !waiversReady"
            @click="submit"
          >
            {{ busy ? '提交中…' : '发起移交量产' }}
          </button>
        </div>
        <p v-if="!waiversReady" class="closure-hint">每项都写满 5 个字才能提交。</p>
      </div>

      <p v-else class="closure-hint">发起结项要项目经理或管理员。</p>
    </template>
  </section>
</template>

<style scoped>
.closure { padding: 18px 20px; border: 1px solid var(--line); border-radius: 6px; background: var(--surface); }
.closure.is-closed { border-color: #bfdbfe; background: linear-gradient(180deg, #f5f9ff, var(--surface)); }
.closure-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.closure-eyebrow { color: var(--brand, #1d4ed8); font-size: 11px; font-weight: 700; letter-spacing: 1px; }
.closure-head h3 { margin: 4px 0 0; font-size: 16px; font-weight: 700; }
.closure-hint { margin: 6px 0 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
.closure-sign { margin: 6px 0 0; color: var(--ink); font-size: 13px; font-weight: 600; }

.closure-timeline { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 18px; }
.closure-timeline div { padding: 12px; border-radius: 5px; background: #fff; border: 1px solid var(--line); text-align: center; }
.closure-timeline b { display: block; font-size: 15px; font-weight: 700; }
.closure-timeline span { color: var(--muted); font-size: 11px; }

.closure-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 10px; margin: 12px 0 0; }
.closure-stats div { padding: 10px 12px; border-radius: 5px; background: #fff; border: 1px solid var(--line); }
.closure-stats dt { color: var(--muted); font-size: 11px; }
.closure-stats dd { margin: 3px 0 0; font-size: 15px; font-weight: 700; }

.closure-note { margin: 12px 0 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
.closure-waived, .closure-team { margin-top: 16px; }
.closure-waived h4, .closure-team h4 { margin: 0 0 6px; font-size: 12px; font-weight: 700; color: var(--muted); }
.closure-waived ul { margin: 0; padding-left: 18px; font-size: 12px; line-height: 1.8; }
.closure-team span { display: inline-block; margin: 0 10px 6px 0; font-size: 12px; }
.closure-team em { margin-left: 5px; color: var(--muted); font-style: normal; }

.closure-checks { list-style: none; margin: 16px 0 0; padding: 0; }
.closure-checks li { display: flex; gap: 10px; padding: 9px 0; border-bottom: 1px solid var(--line); font-size: 13px; }
.closure-checks li:last-child { border-bottom: none; }
.closure-checks p { margin: 3px 0 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
.closure-mark { width: 20px; text-align: center; font-weight: 700; }
.closure-checks li.hard .closure-mark { color: #b91c1c; }
.closure-checks li.soft .closure-mark { color: #a16207; }
.closure-checks li.ok .closure-mark { color: #15803d; }
.closure-count { margin-left: 8px; color: #b45309; font-size: 12px; }

.closure-blocked { margin: 14px 0 0; padding: 10px 12px; border-radius: 5px; background: #fef2f2; color: #991b1b; font-size: 12px; line-height: 1.7; }
.closure-pending { margin-top: 14px; padding: 12px; border-radius: 5px; background: #fffbeb; font-size: 13px; }
.closure-submit { margin-top: 14px; }
.closure-waivers h4 { margin: 0 0 8px; font-size: 12px; font-weight: 700; }
.closure-waivers label { display: block; margin-bottom: 8px; }
.closure-waivers span { display: block; margin-bottom: 3px; color: var(--muted); font-size: 12px; }
.closure-waivers input, .closure textarea { width: 100%; padding: 7px 9px; border: 1px solid var(--line); border-radius: 4px; font-size: 13px; font-family: inherit; }
.closure textarea { margin-top: 8px; }
.closure-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 10px; }
</style>
